import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { genericSubmitHandler } from 'components/organisms/FormsSlice/Forms.helper';
import Copytext from 'components/molecules/Copytext/Copytext';
import { CheckboxInput } from 'components/atoms/CheckboxInput';
import { DefaultForm } from 'components/molecules/DefaultForm';
import Column from 'components/layout/Grid/Column';
import {
  FormColumn,
  FormError,
  HeadlineLabel,
  InputSubmit,
  InputTextArea,
} from 'components/organisms/FormsSlice/Forms/Forms';
import { NachlassFormInputTypes, FormParentProps } from './NachlassForm.types';
import { toast } from 'react-toastify';
import { Loading } from 'components/atoms/Loading';
import { FormSuccessNotification } from 'components/molecules/FormSuccessNotification';
import { RichText } from 'components/atoms/RichText';
import { isRichTextEmpty } from 'prismic/helpers/isRichTextEmpty';
import { WrapperRBProbate } from './NachlassForm.styles';
import { Heading } from 'components/atoms/Heading';
import getHeadingSkin from 'helpers/getHeadingSkin';
import getHeadingTag from 'helpers/getHeadingTag';

export const NachlassForm: React.FC<FormParentProps> = ({
  introRichText,
  titleNotification,
  textNotification,
}) => {
  const { t, lang } = useTranslation('common');
  const [showForm, setShowForm] = useState<boolean>(true);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<NachlassFormInputTypes>({ mode: 'onSubmit' });
  const probateDocumentation = watch('probateDocumentation');
  const probateGuide = watch('probateGuide');
  const probateContact = watch('probateContact');
  const introRichTextContent = Array.isArray(introRichText)
    ? (() => {
        const blocks = [...introRichText];
        let startIndex = 0;
        while (
          blocks[startIndex] &&
          ['heading1', 'heading2', 'heading3', 'heading4'].includes(blocks[startIndex].type)
        ) {
          startIndex += 1;
        }
        return blocks.slice(startIndex);
      })()
    : introRichText;
  const isIntroRichTextEmpty = isRichTextEmpty(introRichTextContent);
  useEffect(() => {
    if (probateDocumentation || probateGuide || probateContact) {
      clearErrors('probateSelection');
    }
  }, [probateDocumentation, probateGuide, probateContact, clearErrors]);
  const submitHandler = async (data: NachlassFormInputTypes) => {
    const hasProbateSelection =
      data.probateDocumentation || data.probateGuide || data.probateContact;
    if (!hasProbateSelection) {
      setError('probateSelection', { type: 'required' });
      return;
    }
    clearErrors('probateSelection');
    if (data.probateContact && !data.phone) {
      setError('phone', { type: 'required' });
      return;
    }
    try {
      const response = await genericSubmitHandler({ data });
      setShowForm(false);
      if (!response.data.ok) {
        setShowForm(true);
        toast.error(t('forms.responses.400'));
      }
    } catch (error) {
      setShowForm(true);
    }
  };

  const onClickReturn = (): void => {
    reset();
    setShowForm(true);
  };

  return (
    <>
      {!showForm && (
        <Column span={12}>
          <FormColumn span={12}>
            <FormSuccessNotification
              onClickReturn={onClickReturn}
              titleNotification={titleNotification}
              textNotification={textNotification}
            />
          </FormColumn>
        </Column>
      )}
      {showForm && (
        <>
          <FormColumn span={12}>
            <Heading skin={getHeadingSkin('heading2')} elementType={getHeadingTag('heading2')}>
              {t('nachlassForm.labels.title')}
            </Heading>
          </FormColumn>
          {!isIntroRichTextEmpty && (
            <FormColumn span={12}>
              <RichText rich_text={introRichTextContent} />
            </FormColumn>
          )}
          <Column span={12}>
            <form onSubmit={handleSubmit(submitHandler)}>
              <FormColumn span={12}>
                <WrapperRBProbate>
                  <CheckboxInput
                    name="probateDocumentation"
                    register={register}
                    translation="nachlassForm.labels.probate.documentation"
                    variant="radio"
                  />
                  <CheckboxInput
                    name="probateGuide"
                    register={register}
                    translation="nachlassForm.labels.probate.guide"
                    variant="radio"
                  />
                  <CheckboxInput
                    name="probateContact"
                    register={register}
                    translation="nachlassForm.labels.probate.contact"
                    variant="radio"
                  />
                </WrapperRBProbate>
                {errors.probateSelection && isSubmitted && (
                  <FormError className="probateError">
                    {t('nachlassForm.errors.probateRequired')}
                  </FormError>
                )}
              </FormColumn>
              <FormColumn span={12}>
                <HeadlineLabel htmlFor="message">
                  {t('contactForm.labels.yourMessage')}
                </HeadlineLabel>
                <InputTextArea
                  {...register('message')}
                  id="message"
                  placeholder={t('nachlassForm.placeholders.message')}
                />
              </FormColumn>
              <DefaultForm errors={errors} register={register} isSubmitted={isSubmitted} />
              {lang === 'de' && (
                <>
                  <FormColumn span={12}>
                    <CheckboxInput
                      name="newsletterSubscribeBallenberg"
                      register={register}
                      translation="nachlassForm.labels.newsletter.ballenberg"
                    />
                  </FormColumn>
                  <FormColumn span={12}>
                    <CheckboxInput
                      name="newsletterSubscribeKurse"
                      register={register}
                      translation="nachlassForm.labels.newsletter.kurse"
                    />
                  </FormColumn>
                </>
              )}
              <FormColumn span={12}>
                {!isSubmitting && (
                  <InputSubmit
                    disabled={isSubmitting}
                    type="submit"
                    value={t('contactForm.labels.submit')}
                  />
                )}
                {isSubmitting && <Loading />}
              </FormColumn>
              <FormColumn span={12}>
                <Copytext textstring={t('forms.requiredFields')} />
              </FormColumn>
              <input type="hidden" value="03-Nachlass" {...register('category')} />
            </form>
          </Column>
        </>
      )}
    </>
  );
};
